import { Editor, Extensions } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { PageType } from 'api/page-template/types';
import { FC } from 'react';
import { AskAiCommandOptions } from './extensions/ask-ai/commands/types';
import { AutoSaveOptions } from './extensions/auto-save/extension';
import { RlCitationOptions } from './extensions/citation/extension';
import { CommandPickerExtensionOptions } from './extensions/command-picker/types';
import { CommandPickerWithTriggerOptions } from './extensions/command-picker-with-trigger/types';
import { CustomGroupSectionNodeOptions } from './extensions/custom-group-section/types';
import { CustomSectionNodeOptions } from './extensions/custom-section/types';
import { DistractionFreeExtensionOptions } from './extensions/distraction-free/extension';
import { SectionMenuOptions } from './extensions/section-menu/extension';
import { VersionHistoryTriggerOptions } from './extensions/version-history-trigger/extension';
import { DefaultFloatingToolbarConfig as DefaultFloatingToolbarConfigType } from './toolbar/types';
import {
  RichTextEditorProps,
  RichTextEditorValueType,
} from '../../../module/ui/components/atoms/rich-text-editor/types';

export type EditorNodeInfo = {
  node: Node;
  start: number;
  end: number;
  contentStart: number;
  contentEnd: number;
};

export type AskAiExtensionConfig =
  | {
      enabled: false;
    }
  | ({
      enabled: true;
    } & AskAiCommandOptions);

export type CommandPickerConfig =
  | {
      enabled: false;
    }
  | ({
      enabled: true;
    } & CommandPickerExtensionOptions);
export type CommandPickerWithTriggerConfig =
  | {
      enabled: false;
    }
  | ({
      enabled: true;
    } & CommandPickerWithTriggerOptions);
export type LikeDislikeExtensionConfig = {
  enabled: boolean;
};
export type AutoSaveExtensionConfig =
  | {
      enabled: false;
    }
  | ({
      enabled: true;
    } & AutoSaveOptions);
export type ExtensionConfig = {
  askAiConfig: AskAiExtensionConfig;
  distractionFreeConfig: DistractionFreeExtensionOptions;
  commandsPickerConfig: CommandPickerConfig;
  customSectionConfig: CustomSectionNodeOptions;
  groupSectionConfig: CustomGroupSectionNodeOptions;
  likeDislikeConfig: LikeDislikeExtensionConfig;
  commandPickerWithTriggerConfig: CommandPickerWithTriggerConfig;
  elementInspectorConfig: {
    enabled?: boolean;
    pageType?: PageType;
    sourceId?: number;
    /** From meta.documentSourceMappingModel.metadata.sources */
    sources?: Array<{
      sourceId: number;
      sourceType: string;
      timestamp?: string;
    }>;
  };
  endOfDocumentConfig: {
    enabled: boolean;
  };
  autoSaveConfig: AutoSaveExtensionConfig;
  tableToolbarConfig: {
    enabled: boolean;
  };
  tableOfContentsConfig: {
    enabled: boolean;
  };
  sectionMenuConfig: SectionMenuOptions;
  versionHistoryConfig: VersionHistoryTriggerOptions;
  citationConfig: RlCitationOptions;
  imageConfig: {
    documentId?: number;
    onImageFiles?: (editor: Editor, files: File[], pos: number) => void;
  };
};
export const defaultExtensionConfig: ExtensionConfig = {
  askAiConfig: {
    enabled: false,
  },
  likeDislikeConfig: {
    enabled: false,
  },
  distractionFreeConfig: {
    onDistractionFreeStateChange: () => {},
  },
  commandsPickerConfig: {
    enabled: false,
  },
  customSectionConfig: {
    allowResolve: false,
    generateTemplateSectionId: false,
  },
  groupSectionConfig: {
    allowResolve: false,
    generateTemplateSectionId: false,
  },
  commandPickerWithTriggerConfig: {
    enabled: false,
  },
  endOfDocumentConfig: {
    enabled: false,
  },
  autoSaveConfig: {
    enabled: false,
  },
  tableToolbarConfig: {
    enabled: false,
  },
  elementInspectorConfig: {
    enabled: false,
    pageType: undefined,
    sourceId: undefined,
    sources: undefined,
  },
  tableOfContentsConfig: {
    enabled: false,
  },
  sectionMenuConfig: {
    enabled: false,
  },
  versionHistoryConfig: {
    enabled: false,
  },
  citationConfig: {},
  imageConfig: {},
};

type BaseFloatingToolbarConfig = {
  enabled: true;
};
export type DefaultDocumentFloatingToolbarConfig = BaseFloatingToolbarConfig &
  DefaultFloatingToolbarConfigType;
export type CustomDocumentFloatingToolbarConfig = BaseFloatingToolbarConfig & {
  toolbar: FC<{ editor: Editor }>;
};
type EnabledFloatingToolbarConfig =
  | DefaultDocumentFloatingToolbarConfig
  | CustomDocumentFloatingToolbarConfig;

export type DocumentEditorFloatingToolbarConfig =
  | {
      enabled: false;
    }
  | EnabledFloatingToolbarConfig;
export const defaultDocumentFloatingToolbarConfig: DocumentEditorFloatingToolbarConfig =
  {
    enabled: true,
  };
export type DocumentEditorProps<ValueType extends RichTextEditorValueType> =
  RichTextEditorProps<ValueType> & {
    floatingToolbarConfig?: DocumentEditorFloatingToolbarConfig;
    extensionConfig?: Partial<ExtensionConfig>;
    extensions?: Extensions;
    canDragAndDrop?: boolean;
    isInsertOnlyMode?: boolean;
    feedbackPayload?: {
      sourceId: string;
      sourceType: string;
      feedbackData: any[];
    };
    isChatOpen?: boolean;
    pageType?: PageType;
  };
